import './FilterOptionListItem.scss';

import ArrowButton from '@components/ArrowButton';
import type { FilterOptionItemSettings } from '@components/FilterOptionItem';
import { FilterOptionItem, useFilterOptionItem } from '@components/FilterOptionItem';
import FilterOptionItemButton from '@components/FilterOptionItem/Button';
import FilterOptionItemLabel from '@components/FilterOptionItem/Label';
import type { FilterOptionType } from '@providers/FilterProvider';
import { FILTER_OPTION_TYPE } from '@providers/FilterProvider';
import { createClsNameMap, formatLabelFilterOption, mapModifiers, stripHtml } from '@utils';
import { useCallback, useState } from 'react';

export const clsNameMap = createClsNameMap({
  modifiers: ['disabled'],
  elements: {
    sublist: createClsNameMap(),
  },
})('filter-option-list-option-item');

export type FilterOptionListItemProps = {
  value: string;
  label?: string;
  docCount?: number;
  settings: FilterOptionItemSettings;
  items?: Array<ChildProps>;
  option?: FilterOptionType;
  handle?: string;
  rateCurrency?: number;
  /**
   * @description Sublist items
   */
  children?: React.ReactNode;
};

export type ChildProps = Pick<FilterOptionListItemProps, 'value' | 'docCount' | 'items' | 'handle'>;

type State = {
  isChildListExpanded: boolean;
};

const FilterOptionListItem = ({
  value,
  label,
  handle,
  docCount,
  option,
  settings,
  children,
  items,
}: FilterOptionListItemProps) => {
  const {
    isOptionItemSelected,
    handleOptionItemSelect,
    showOutOfStockOption,
    showFilterOptionCount,
  } = useFilterOptionItem({
    value,
    filterOptionId: option?.filterOptionId || '',
    items,
    handle,
    filterType: option?.filterType,
  });

  const [state, setState] = useState<State>({
    isChildListExpanded: false,
  });

  const toggleChildListExpandedState = useCallback(() => {
    setState((prevState) => ({
      isChildListExpanded: !prevState.isChildListExpanded,
    }));
  }, []);

  const filterTypeNotHaveDocCount = [FILTER_OPTION_TYPE.STOCK];

  if (option && docCount === undefined && filterTypeNotHaveDocCount.includes(option.filterType)) {
    docCount = 0;
  }

  const disabled = docCount === 0;

  if (!showOutOfStockOption && disabled) return null;

  if (!stripHtml(value)) return null;

  return (
    <>
      <FilterOptionItem className={mapModifiers(clsNameMap, { disabled })}>
        <FilterOptionItemButton
          as='button'
          isSelected={isOptionItemSelected}
          hasCheckbox={settings.selectType === 'multiple'}
          label={
            <FilterOptionItemLabel
              label={formatLabelFilterOption(label || value, option?.prefix)}
              settings={{
                displayAllValuesInUppercaseForm: settings.displayAllValuesInUppercaseForm,
              }}
            />
          }
          docCount={docCount}
          showFilterOptionCount={showFilterOptionCount}
          onClick={handleOptionItemSelect}
        />
        {!!items?.length && (
          <ArrowButton
            label={`Expand/Collapse ${value}`}
            modifiers={state.isChildListExpanded ? ['bottom'] : ['right']}
            onClick={toggleChildListExpandedState}
            isFilter={true}
          />
        )}
        {children && state.isChildListExpanded && (
          <ul className={clsNameMap.elm('sublist')}>{state.isChildListExpanded && children}</ul>
        )}
      </FilterOptionItem>
    </>
  );
};

export default FilterOptionListItem;
